import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import type { PersonalTemplateListItem, SaveTemplateParams, PersonalTemplate } from '@/types/supremeIntelligenceTypes';
import * as templateStorage from '@/services/templateStorage';
import { toast } from 'sonner';

export const usePersonalTemplates = () => {
  const { isSignedIn } = useAuth();
  const [templates, setTemplates] = useState<PersonalTemplateListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refreshList = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const list = await templateStorage.listUserTemplates();
      setTemplates(list);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load templates');
      setError(error);
      console.error('Error loading templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Sync local templates to cloud on login
  useEffect(() => {
    if (isSignedIn) {
      templateStorage.syncLocalToCloud().then(count => {
        if (count > 0) {
          toast.success(`Synced ${count} local template${count > 1 ? 's' : ''} to cloud`);
          refreshList();
        }
      });
    }
  }, [isSignedIn]);

  useEffect(() => {
    refreshList();
  }, []);

  const saveTemplate = async (params: SaveTemplateParams): Promise<PersonalTemplate> => {
    try {
      setError(null);
      const saved = await templateStorage.saveTemplate(params);
      toast.success('Template saved successfully');
      await refreshList();
      return saved;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to save template');
      setError(error);
      toast.error(error.message);
      throw error;
    }
  };

  const loadTemplate = async (id: string): Promise<PersonalTemplate | null> => {
    try {
      setError(null);
      const template = await templateStorage.loadTemplate(id);
      if (!template) {
        toast.error('Template not found');
        return null;
      }
      return template;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load template');
      setError(error);
      toast.error(error.message);
      return null;
    }
  };

  const deleteTemplate = async (id: string): Promise<void> => {
    try {
      setError(null);
      const success = await templateStorage.deleteTemplate(id);
      if (success) {
        toast.success('Template deleted');
        await refreshList();
      } else {
        throw new Error('Failed to delete template');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete template');
      setError(error);
      toast.error(error.message);
      throw error;
    }
  };

  const updateTemplate = async (id: string, updates: Partial<SaveTemplateParams>): Promise<void> => {
    try {
      setError(null);
      const updated = await templateStorage.updateTemplate(id, updates);
      if (updated) {
        toast.success('Template updated');
        await refreshList();
      } else {
        throw new Error('Failed to update template');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update template');
      setError(error);
      toast.error(error.message);
      throw error;
    }
  };

  return {
    templates,
    isLoading,
    error,
    saveTemplate,
    loadTemplate,
    deleteTemplate,
    updateTemplate,
    refreshList
  };
};
